import { STACK_FEATURES, type StackFeature } from './stackConfig'

export type { StackFeature }

/** Pure stack rules (no React). See README.md in this folder. */

const STACK_SESSION_STORAGE_KEY = 'stack-navigation-entries'

export type StackEntry = {
  pathname: string
  search: string
  featureId: string | null
  /** Extra data for this screen (e.g. where the user came from). Survives Back. */
  state: Record<string, unknown>
}

export type PushOptions = {
  replace?: boolean
  state?: Record<string, unknown>
}

export type StackApi = {
  entries: StackEntry[]
  current: StackEntry | null
  previous: StackEntry | null
  canGoBack: boolean
  push: (path: string, options?: PushOptions) => void
  replace: (path: string, state?: Record<string, unknown>) => void
  pop: () => void
  /** Drop every frame for a flow. Does not change the URL. Optional `then` runs on the next microtask so a following navigate sees the cleared stack. */
  resetFeature: (featureId: string, then?: () => void) => void
}

// ============================================================================
// FEATURE LOOKUP
// Match a URL to a row in stackConfig. Used to know which flow owns a screen.
// ============================================================================

export function findFeatureForPath(
  pathname: string,
  features: readonly StackFeature[] = STACK_FEATURES
): StackFeature | null {
  for (const feature of features) {
    if (pathname === feature.rootPath || pathname.startsWith(`${feature.rootPath}/`)) {
      return feature
    }
  }
  return null
}

function isFeatureRootPath(pathname: string, feature: StackFeature): boolean {
  return pathname === feature.rootPath
}

function findFeatureById(
  featureId: string,
  features: readonly StackFeature[]
): StackFeature | null {
  return features.find((feature) => feature.id === featureId) ?? null
}

// ============================================================================
// CREATE A STACK ENTRY
// One frame in the back stack: URL + which flow it belongs to + optional state.
// ============================================================================

export function createStackEntryFromPath(
  path: string,
  features: readonly StackFeature[] = STACK_FEATURES,
  state: Record<string, unknown> = {}
): StackEntry {
  const [pathnamePart, searchPart] = path.split('?')
  const pathname = pathnamePart || '/'
  const search = searchPart ? `?${searchPart}` : ''
  return createStackEntryFromLocation(pathname, search, features, state)
}

export function createStackEntryFromLocation(
  pathname: string,
  search: string,
  features: readonly StackFeature[] = STACK_FEATURES,
  state: Record<string, unknown> = {}
): StackEntry {
  const feature = findFeatureForPath(pathname, features)
  return {
    pathname,
    search: search ?? '',
    featureId: feature?.id ?? null,
    state
  }
}

export function isSameLocation(entry: StackEntry, pathname: string, search: string): boolean {
  return entry.pathname === pathname && entry.search === (search ?? '')
}

// ============================================================================
// STACK UPDATES
// Pure functions. They return a new array. They never call React Router.
//
// Rules:
// 1. Landing on a flow's rootPath clears that flow's inner screens.
// 2. Switching to a different flow drops the previous flow's inner screens.
// 3. pop() only pops when the previous screen is in the same flow.
// ============================================================================

export function canGoBackInStack(entries: StackEntry[]): boolean {
  if (entries.length < 2) return false
  const currentEntry = entries[entries.length - 1]
  const previousEntry = entries[entries.length - 2]
  if (!currentEntry?.featureId || !previousEntry) return false
  return previousEntry.featureId === currentEntry.featureId
}

export function removeFeatureFromStack(entries: StackEntry[], featureId: string): StackEntry[] {
  return entries.filter((entry) => entry.featureId !== featureId)
}

export function pushToStack(
  entries: StackEntry[],
  nextEntry: StackEntry,
  replace: boolean,
  features: readonly StackFeature[]
): StackEntry[] {
  const nextFeature = nextEntry.featureId
    ? findFeatureById(nextEntry.featureId, features)
    : null

  // Feature root (rootPath). Drop this feature's inner screens so Back from
  // the root cannot return into a nested screen of the same flow.
  if (nextFeature && isFeatureRootPath(nextEntry.pathname, nextFeature)) {
    return appendIfNew(removeFeatureFromStack(entries, nextFeature.id), nextEntry)
  }

  const topEntry = entries[entries.length - 1]
  // Moved to a different feature (or a non-feature page). Drop the previous
  // feature so its screens cannot sit under the new one.
  if (topEntry?.featureId && topEntry.featureId !== nextEntry.featureId) {
    const withoutCurrentFeature = removeFeatureFromStack(entries, topEntry.featureId)
    if (nextEntry.featureId) return appendIfNew(withoutCurrentFeature, nextEntry)
    return putEntry(withoutCurrentFeature, nextEntry, true)
  }

  // Same feature (or no feature): push, or replace the top if replace=true.
  return putEntry(entries, nextEntry, replace)
}

/**
 * URL changed without going through push/back (refresh, deep link, browser back).
 * Rebuild the stack so it matches the URL — never leave a "ghost" extra frame.
 */
export function syncStackWithLocation(
  entries: StackEntry[],
  pathname: string,
  search: string,
  features: readonly StackFeature[]
): StackEntry[] {
  const locationEntry = createStackEntryFromLocation(pathname, search, features)
  const topEntry = entries[entries.length - 1]

  // Already pointing at this URL. Nothing to do.
  if (topEntry && isSameLocation(topEntry, pathname, search)) return entries

  // Browser back: the URL is the previous stack frame. Just pop.
  if (entries.length >= 2) {
    const previousEntry = entries[entries.length - 2]
    if (previousEntry && isSameLocation(previousEntry, pathname, search)) {
      return entries.slice(0, -1)
    }
  }

  const feature = findFeatureForPath(pathname, features)

  // Feature root: drop that feature's inner screens, keep this root entry.
  if (feature && isFeatureRootPath(pathname, feature)) {
    return appendIfNew(removeFeatureFromStack(entries, feature.id), locationEntry)
  }

  // Nested screen of a feature (refresh / deep link). Drop that feature's
  // old frames so we do not keep extra history under this URL.
  if (feature) {
    return [...removeFeatureFromStack(entries, feature.id), locationEntry]
  }

  // Not a registered feature. Drop feature frames, replace the top.
  const withoutFeatures = entries.filter((entry) => entry.featureId == null)
  return putEntry(withoutFeatures, locationEntry, true)
}

function appendIfNew(entries: StackEntry[], nextEntry: StackEntry): StackEntry[] {
  const topEntry = entries[entries.length - 1]
  if (topEntry && isSameLocation(topEntry, nextEntry.pathname, nextEntry.search)) return entries
  return [...entries, nextEntry]
}

function putEntry(entries: StackEntry[], nextEntry: StackEntry, replace: boolean): StackEntry[] {
  const topEntry = entries[entries.length - 1]

  // Same URL as the top. Push is a no-op. Replace keeps the URL and merges state.
  if (topEntry && isSameLocation(topEntry, nextEntry.pathname, nextEntry.search)) {
    if (!replace) return entries
    return [
      ...entries.slice(0, -1),
      { ...nextEntry, state: { ...topEntry.state, ...nextEntry.state } }
    ]
  }

  if (replace) {
    if (entries.length === 0) return [nextEntry]
    return [...entries.slice(0, -1), nextEntry]
  }

  return [...entries, nextEntry]
}

// ============================================================================
// SESSION STORAGE
// Survive refresh. Feature is always derived from the URL.
// We restore pathname, search, and state.
// ============================================================================

export function loadStackFromStorage(
  currentEntry: StackEntry,
  features: readonly StackFeature[] = STACK_FEATURES
): StackEntry[] {
  const savedEntries = readSavedEntries(features)
  if (savedEntries.length === 0) return [currentEntry]

  const savedTop = savedEntries[savedEntries.length - 1]
  // Refresh on the same URL: restore the full saved stack (Back still works).
  if (savedTop && isSameLocation(savedTop, currentEntry.pathname, currentEntry.search)) {
    return savedEntries
  }

  // Saved stack is for a different URL. Same as a deep link: one frame for
  // the current page, no leftover inner screens of this flow.
  if (currentEntry.featureId) {
    return [...removeFeatureFromStack(savedEntries, currentEntry.featureId), currentEntry]
  }

  const withoutFeatures = savedEntries.filter((entry) => entry.featureId == null)
  return putEntry(withoutFeatures, currentEntry, true)
}

export function saveStackToStorage(entries: StackEntry[]) {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(STACK_SESSION_STORAGE_KEY, JSON.stringify(entries))
  } catch {
    // In-memory stack still works if sessionStorage is blocked.
  }
}

function readSavedEntries(features: readonly StackFeature[]): StackEntry[] {
  if (typeof window === 'undefined') return []
  try {
    const rawValue = window.sessionStorage.getItem(STACK_SESSION_STORAGE_KEY)
    if (!rawValue) return []
    const parsed = JSON.parse(rawValue)
    if (!Array.isArray(parsed)) return []

    const entries: StackEntry[] = []
    for (const item of parsed) {
      const entry = parseSavedEntry(item, features)
      if (entry) entries.push(entry)
    }
    return entries
  } catch {
    return []
  }
}

function parseSavedEntry(item: unknown, features: readonly StackFeature[]): StackEntry | null {
  if (!item || typeof item !== 'object') return null
  const raw = item as Record<string, unknown>
  if (typeof raw.pathname !== 'string') return null

  const pathname = raw.pathname
  const search = typeof raw.search === 'string' ? raw.search : ''
  const state =
    raw.state && typeof raw.state === 'object' ? (raw.state as Record<string, unknown>) : {}

  return createStackEntryFromLocation(pathname, search, features, state)
}
